import { gql } from 'graphql-request'

export const EQUIPMENT_COMPLIANCE_QUERY_KEYS = {
  report: 'EQUIPMENT_COMPLIANCE_REPORT',
  bulkSendPreview: 'POLICY_BULK_SEND_PREVIEW',
  bulkSendRun: 'POLICY_BULK_SEND_RUN',
  evidenceExports: 'POLICY_EVIDENCE_EXPORTS',
} as const

const ITEM_FIELDS = `
  kind
  title
  reasonCode
  dueOn
`

const BULK_RUN_FIELDS = `
  runId
  siteId
  status
  totalCount
  processedCount
  queuedCount
  skippedCount
  skipReasons {
    reason
    count
  }
  estimatedSegments
  estimatedCredits
  createdAt
  completedAt
  cancelledAt
`

const EXPORT_FIELDS = `
  exportId
  siteId
  status
  requestedBy
  signedFrom
  signedTo
  includeScans
  itemCount
  downloadCount
  createdAt
  completedAt
  expiresAt
  revokedAt
`

export const equipmentComplianceReportQuery = gql`
  query EquipmentComplianceReport(
    $siteId: ID!
    $filter: EquipmentComplianceReportFilterInput
    $sort: EquipmentComplianceSort
    $sortDirection: EquipmentComplianceSortDirection
    $page: EquipmentComplianceReportPageInput
  ) {
    equipmentComplianceReport(
      siteId: $siteId
      filter: $filter
      sort: $sort
      sortDirection: $sortDirection
      page: $page
    ) {
      siteId
      evaluatedAt
      categories
      summary {
        total
        ready
        renewalDue
        blocked
      }
      totalCount
      offset
      limit
      redacted
      rows {
        clientId
        firstName
        lastName
        lastVisitAt
        status
        categories {
          equipmentCategory
          status
          decision
          reasonCodes
          blockingItems {
            ${ITEM_FIELDS}
          }
          renewalItems {
            ${ITEM_FIELDS}
          }
        }
      }
    }
  }
`

export const policyBulkSendPreviewQuery = gql`
  query PolicyBulkSendPreview(
    $siteId: ID!
    $equipmentCategory: PolicyEquipmentScope
    $statuses: [EquipmentComplianceStatus!]
    $search: String
  ) {
    policyBulkSendPreview(
      siteId: $siteId
      equipmentCategory: $equipmentCategory
      statuses: $statuses
      search: $search
    ) {
      siteId
      matchedClients
      recipients
      toGuardians
      skipped
      skipReasons {
        reason
        count
      }
      segmentsPerMessage
      estimatedSegments
      estimatedCredits
      availableCredits
      cap
      exceedsCap
    }
  }
`

export const policyBulkSendRunQuery = gql`
  query PolicyBulkSendRun($siteId: ID!, $runId: ID!) {
    policyBulkSendRun(siteId: $siteId, runId: $runId) {
      ${BULK_RUN_FIELDS}
    }
  }
`

export const startPolicyBulkSendMutation = gql`
  mutation StartPolicyBulkSend(
    $siteId: ID!
    $equipmentCategory: PolicyEquipmentScope
    $statuses: [EquipmentComplianceStatus!]
    $search: String
  ) {
    startPolicyBulkSend(
      siteId: $siteId
      equipmentCategory: $equipmentCategory
      statuses: $statuses
      search: $search
    ) {
      ${BULK_RUN_FIELDS}
    }
  }
`

export const cancelPolicyBulkSendMutation = gql`
  mutation CancelPolicyBulkSend($siteId: ID!, $runId: ID!) {
    cancelPolicyBulkSend(siteId: $siteId, runId: $runId) {
      ${BULK_RUN_FIELDS}
    }
  }
`

export const policyEvidenceExportsQuery = gql`
  query PolicyEvidenceExports($siteId: ID!) {
    policyEvidenceExports(siteId: $siteId) {
      ${EXPORT_FIELDS}
    }
  }
`

export const requestPolicyEvidenceExportMutation = gql`
  mutation RequestPolicyEvidenceExport(
    $siteId: ID!
    $signedFrom: String!
    $signedTo: String!
    $includeScans: Boolean!
  ) {
    requestPolicyEvidenceExport(
      siteId: $siteId
      signedFrom: $signedFrom
      signedTo: $signedTo
      includeScans: $includeScans
    ) {
      ${EXPORT_FIELDS}
    }
  }
`

export const downloadPolicyEvidenceExportMutation = gql`
  mutation DownloadPolicyEvidenceExport($siteId: ID!, $exportId: ID!) {
    downloadPolicyEvidenceExport(siteId: $siteId, exportId: $exportId) {
      exportId
      url
      expiresAt
    }
  }
`

export const revokePolicyEvidenceExportMutation = gql`
  mutation RevokePolicyEvidenceExport($siteId: ID!, $exportId: ID!) {
    revokePolicyEvidenceExport(siteId: $siteId, exportId: $exportId) {
      ${EXPORT_FIELDS}
    }
  }
`
